<template>
  <div
    :class="['flex gap-2 px-4 py-2', message.role === 'user' ? 'justify-end' : '']"
    :style="animationDelay ? { animationDelay: `${animationDelay}ms` } : undefined"
  >
    <!-- Tool call/result -->
    <div
      v-if="message.role === 'tool'"
      class="w-full max-w-[90%]"
    >
      <!-- Visualization component (chart, table, etc.) -->
      <component
        :is="visualizationComponent"
        v-if="visualizationComponent && message.toolInput"
        :tool-input="message.toolInput"
        :tool-result="message.toolResult"
      />
      <!-- Default tool result card -->
      <AiToolResult
        v-else
        :tool-name="message.toolName"
        :tool-input="message.toolInput"
        :tool-result="message.toolResult"
      />
    </div>

    <!-- User message -->
    <div
      v-else-if="message.role === 'user'"
      class="group/msg relative max-w-[80%]"
    >
      <div class="rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">
        {{ message.content }}
      </div>
      <AiMessageActions
        role="user"
        @copy="$emit('copy', message.content)"
        @edit-resend="$emit('editResend', message.content)"
      />
    </div>

    <!-- Assistant message -->
    <div
      v-else
      class="group/msg relative flex max-w-[90%] gap-2"
    >
      <div class="mt-1 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
        <Sparkles :class="['size-3 text-primary', isActivelyStreaming && 'animate-pulse']" />
      </div>
      <div class="min-w-0 flex-1">
        <!-- Streaming text with cursor -->
        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="message.content"
          :class="[
            `
              ai-message-content prose prose-sm max-w-none break-words text-foreground
              dark:prose-invert
            `,
            isActivelyStreaming && 'ai-streaming',
          ]"
          v-html="renderedContent"
        ></div>
        <!-- eslint-enable vue/no-v-html -->
        <!-- Thinking indicator (no content yet) -->
        <div
          v-else-if="isActivelyStreaming"
          class="flex items-center gap-2 py-1 text-sm text-muted-foreground"
        >
          <span class="thinking-dots flex gap-0.5">
            <span class="size-1.5 rounded-full bg-current"></span>
            <span class="size-1.5 rounded-full bg-current"></span>
            <span class="size-1.5 rounded-full bg-current"></span>
          </span>
          <span>{{ t('ai.thinking') }}</span>
        </div>
      </div>
      <AiMessageActions
        v-if="message.content && !isActivelyStreaming"
        role="assistant"
        @copy="$emit('copy', message.content)"
        @regenerate="$emit('regenerate')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { Sparkles } from 'lucide-vue-next'

import type { AiMessage } from '@/stores/ai'

import AiMessageActions from './AiMessageActions.vue'
import AiToolResult from './AiToolResult.vue'
import { getVisualizationComponent } from './aiVisualizationRegistry'
import { renderMarkdown } from './useAiMarkdown'

const props = defineProps<{
  message: AiMessage
  isActivelyStreaming?: boolean
  animationDelay?: number
}>()

defineEmits<{
  copy: [content: string]
  regenerate: []
  editResend: [content: string]
}>()

const visualizationComponent = computed(() =>
  getVisualizationComponent(props.message.toolName),
)

const { t } = useI18n()

const renderedContent = computed(() => {
  if (!props.message.content) return ''
  return renderMarkdown(props.message.content)
})
</script>

<style scoped>
.ai-message-content :deep(> :first-child) {
  margin-top: 0;
}

.ai-message-content :deep(> :last-child) {
  margin-bottom: 0;
}

.ai-message-content :deep(pre) {
  font-size: 0.8125rem;
}

/* Blinking cursor at end of streaming text */
.ai-streaming :deep(> :last-child::after) {
  content: '▍';
  animation: blink 0.7s steps(1) infinite;
  color: hsl(var(--primary));
  font-weight: 400;
  margin-left: 1px;
}

@keyframes blink {
  50% {
    opacity: 0;
  }
}

/* Thinking dots animation */
.thinking-dots span {
  animation: dot-bounce 1.4s ease-in-out infinite;
}

.thinking-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.thinking-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dot-bounce {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
